'use client'; // ระบุว่าไฟล์นี้เป็น Client Component (ใช้ hook ได้, รันบน browser)
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import useEmblaCarousel from 'embla-carousel-react';
import './product-showboxes.css';
import {
    ProductGroupAttb,
    ProductGroupButtonAttb,
    ProductGroupNameStyleType,
} from '@/util/query/product-group-query';

interface ProductGroupItem {
    id: string;
    priority: number;
    groupType: string;
    collectionId: string;
    collectionSortId?: string;
    collectionHandle?: string;
    attributes: ProductGroupAttb;
    enableButton: boolean;
    buttonAttributes: ProductGroupButtonAttb;
}

interface ProductsGroupData {
    id?: string | number;
    tabEnable?: boolean;
    productGroupItem?: ProductGroupItem[];
    productGroupNameStyle?: ProductGroupNameStyleType;
    productGroupStyle?: { displayStyle?: string };
}

interface HeaderAttribute {
    data?: string;
    description?: string;
}
interface HeaderData {
    attribute?: HeaderAttribute;
}

interface ProductShowBoxesTabProps {
    productsGroupData: ProductsGroupData;
    colorStyle: any;
    instanceKey?: string;
    disableUrlSync?: boolean;
    shortParam?: boolean;
    headerData: HeaderData;
    radiusStyle: any;
}

// จำกัด id ที่ใช้บน DOM ไม่ให้มีอักขระพิเศษที่อาจรบกวน selector / เกิดปัญหา XSS ผ่าน attribute (React ปกติ escape อยู่แล้ว แต่ทำเชิงป้องกันเพิ่ม)
function sanitizeDomId(raw: unknown): string | undefined {
    if (raw === undefined || raw === null) return undefined;
    const s = String(raw);
    // เหลือแค่ ตัวอักษร ตัวเลข _ -
    const cleaned = s.replace(/[^0-9A-Za-z_-]/g, '').slice(0, 40); // จำกัดความยาว
    return cleaned || undefined;
}

// ป้องกัน index หลุดขอบหรือ NaN
function normalizeIndex(index: number, length: number): number | null {
    if (!Number.isInteger(index)) return null;
    if (index < 0 || index >= length) return null;
    return index;
}

const ProductShowBoxesTab = ({
    productsGroupData,
    colorStyle,
    instanceKey,
    disableUrlSync,
    shortParam = true,
    headerData,
    radiusStyle,
}: ProductShowBoxesTabProps) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();

    const [emblaTab, emblaTabApi] = useEmblaCarousel({
        dragFree: true,
    });
    const [emblaWrapTab, emblaWrapTabApi] = useEmblaCarousel({
        dragFree: true,
    });

    const [canScroll, setCanScroll] = useState(false); // เก็บสถานะว่า carousel สามารถเลื่อนได้หรือไม่ (ใช้ตัดสิน class alignment)
    const [tabId, setTabId] = useState<number>(0); // เก็บ index ของ tab ปัจจุบัน (ไม่ใช่ id จริงใน data)
    // สร้างชื่อ query param ให้ unique ต่อ instance (ป้องกันการชนกันของหลาย component ในหน้าเดียว)
    const tabParamName = React.useMemo(() => {
        if (disableUrlSync) return null; // ถ้าปิด sync ก็ไม่ต้องคำนวณ
        const base = shortParam ? 't' : 'tabId'; // เลือก prefix ตามรูปแบบที่ผู้ใช้ต้องการ
        const key = instanceKey || productsGroupData?.id || ''; // ใช้ instanceKey ถ้ามี ไม่งั้น fallback เป็น id จาก data
        return key ? `${base}_${key}` : base; // ได้ string สุดท้าย เช่น t_123 / tabId_abc
    }, [instanceKey, productsGroupData?.id, disableUrlSync, shortParam]);

    const productGroupItems: ProductGroupItem[] = Array.isArray(
        productsGroupData?.productGroupItem,
    )
        ? productsGroupData.productGroupItem
        : [];

    const groupNamePosition: 'start' | 'end' | 'center' = (():
        | 'start'
        | 'end'
        | 'center' => {
        const pos = productsGroupData?.productGroupNameStyle?.position;
        switch (pos) {
            case 'LEFT':
                return 'start';
            case 'RIGHT':
                return 'end';
            case 'CENTER':
                return 'center';
            default:
                return 'start';
        }
    })();

    const groupNameDisplayStyle: 'nowrap' | 'wrap' = ((): 'nowrap' | 'wrap' => {
        const ds = productsGroupData?.productGroupNameStyle?.displayStyle;
        switch (ds) {
            case 'SINGLE_LINE':
                return 'nowrap';
            case 'MULTI_LINE':
                return 'wrap';
            default:
                return 'nowrap';
        }
    })();

    useEffect(() => {
        if (!emblaTabApi) return;
        if (!productsGroupData?.id) return; // ถ้าไม่มี id จะหา element ไม่ได้

        const checkScroll = () => {
            const scrollable =
                emblaTabApi.canScrollNext() || emblaTabApi.canScrollPrev();
            setCanScroll(scrollable);

            const el = document.getElementById(
                `cmp-product-group-name-nowrap-${productsGroupData.id}`,
            );
            if (el) {
                el.classList.remove(
                    'product-display-end',
                    'product-display-center',
                    'product-display-start',
                );
                if (scrollable) {
                    el.classList.add('product-display-start');
                } else {
                    if (groupNamePosition === 'end') {
                        el.classList.add('product-display-end');
                    } else if (groupNamePosition === 'center') {
                        el.classList.add('product-display-center');
                    } else {
                        el.classList.add('product-display-start');
                    }
                }
            }
        };

        emblaTabApi.on('reInit', checkScroll);
        emblaTabApi.on('select', checkScroll);
        checkScroll();
    }, [emblaTabApi, canScroll, productGroupItems, tabId, pathname]);

    useEffect(() => {
        if (!tabParamName || disableUrlSync) return;
        const rawParam = searchParams?.get(tabParamName);
        // sanitize: อนุญาตเฉพาะตัวอักษร/ตัวเลข/_- และจำกัดความยาว 10 ตัว ป้องกัน input แปลก ๆ
        const tabIdParam = rawParam
            ? rawParam.replace(/[^0-9a-zA-Z_-]/g, '').slice(0, 10)
            : null;
        if (tabIdParam !== null) {
            let resolvedIndex: number | null = null;
            if (shortParam) {
                // โหมดสั้น: เก็บ index ตรง ๆ
                const num = parseInt(tabIdParam, 10);
                if (!Number.isNaN(num)) resolvedIndex = num;
            } else {
                // โหมดเต็ม: อาจเป็น id หรือ index
                const byIdIndex = productGroupItems.findIndex(
                    (g: any) => String(g?.id) === String(tabIdParam),
                );
                if (byIdIndex >= 0) {
                    resolvedIndex = byIdIndex;
                } else {
                    const num = parseInt(tabIdParam, 10);
                    if (!Number.isNaN(num)) resolvedIndex = num;
                }
            }
            if (
                resolvedIndex !== null &&
                resolvedIndex >= 0 &&
                resolvedIndex < productGroupItems.length &&
                resolvedIndex !== tabId
            ) {
                setTabId(resolvedIndex);
                if (groupNameDisplayStyle === 'nowrap' && emblaTabApi)
                    emblaTabApi.scrollTo(resolvedIndex);
                if (groupNameDisplayStyle === 'wrap' && emblaWrapTabApi)
                    emblaWrapTabApi.scrollTo(resolvedIndex);
            }
        }
    }, [
        searchParams,
        groupNameDisplayStyle,
        productGroupItems,
        tabParamName,
        disableUrlSync,
    ]);

    // ฟังก์ชันเมื่อผู้ใช้คลิก tab
    const handleClickTab = useCallback(
        (index: number, displayStyle: 'nowrap' | 'wrap') => {
            const safeIndex = normalizeIndex(index, productGroupItems.length);
            if (safeIndex === null) return; // กัน index หลุดขอบ / ไม่ถูกต้อง
            // จับว่าเป็นการ "เปลี่ยน" tab จริงไหม (เทียบกับ tab ปัจจุบันก่อน setState)
            const isTabChanged = safeIndex !== tabId;
            setTabId(safeIndex);
            const groupItem = productGroupItems[safeIndex];
            const groupItemId = groupItem?.id;

            if (!disableUrlSync && tabParamName) {
                try {
                    const params = new URLSearchParams(
                        searchParams?.toString(),
                    );
                    if (groupItemId !== undefined) {
                        params.set(
                            tabParamName,
                            shortParam
                                ? String(safeIndex)
                                : String(groupItemId),
                        );
                    } else {
                        params.set(tabParamName, String(safeIndex));
                    }
                    // เมื่อเปลี่ยนหมวดหมู่จริง ต้องรีเซต page กลับหน้า 1 ของหมวดหมู่ใหม่
                    // มิฉะนั้น page param เดิม (เช่นหน้า 2) จะค้างไว้แล้วข้ามหน้า 1 ของหมวดหมู่ใหม่
                    // key ของ page param ผูกกับ productsGroupData.id เดียวกับฝั่ง product list
                    // (product-showboxes-product-list.tsx: pageParamKey = `page${groupIdSuffix}`)
                    // ใช้ delete ให้สอดคล้องกับ convention ใน order-selection.tsx (sort/page-size)
                    if (isTabChanged) {
                        const pageParamKey = productsGroupData?.id
                            ? `page_${productsGroupData.id}`
                            : 'page';
                        params.delete(pageParamKey);
                    }
                    const queryString = params.toString();
                    const nextUrl = `${pathname}${
                        queryString ? `?${queryString}` : ''
                    }`;
                    const isPageStyle =
                        productsGroupData?.productGroupStyle?.displayStyle ===
                        'PAGE';
                    if (isPageStyle) {
                        router.replace(nextUrl, { scroll: false }); // ไม่ scroll หน้าขึ้นบน
                    } else {
                        window.history.replaceState(null, '', nextUrl);
                    }
                } catch (e) {
                    // เงียบ (ไม่ให้ UX สะดุด) ถ้าแก้ URL ไม่สำเร็จ
                }
            }
            if (displayStyle === 'nowrap') {
                if (emblaTabApi) emblaTabApi.scrollTo(safeIndex);
            } else if (displayStyle === 'wrap') {
                if (emblaWrapTabApi) emblaWrapTabApi.scrollTo(safeIndex);
            }
        },
        [
            emblaTabApi,
            emblaWrapTabApi,
            pathname,
            productGroupItems.length,
            productGroupItems,
            router,
            searchParams,
            shortParam,
            tabParamName,
            disableUrlSync,
            tabId,
            productsGroupData?.id,
        ],
    );

    if (!productsGroupData?.tabEnable || productGroupItems.length === 0)
        return null;

    const sanitizedGroupId = sanitizeDomId(productsGroupData?.id);
    return (
        <div
            id={`product-showboxes-component-tab-${sanitizedGroupId}`}
            className={`w-full flex flex-col justify-center items-center gap-1 px-[30px] sm:px-[120px]
            ${
                productGroupItems[tabId]
                    ? headerData &&
                      headerData.attribute &&
                      (headerData.attribute.data ||
                          headerData.attribute.description)
                        ? ''
                        : 'pt-[40px]'
                    : headerData &&
                        headerData.attribute &&
                        (headerData.attribute.data ||
                            headerData.attribute.description)
                      ? 'pb-[40px]'
                      : 'py-[40px]'
            }
        `}
        >
            <div
                id={`product-showboxes-component-tab-embla-${sanitizedGroupId}`}
                className="embla overflow-hidden"
                ref={
                    groupNameDisplayStyle === 'nowrap' ? emblaTab : emblaWrapTab
                }
            >
                <div
                    id={
                        groupNameDisplayStyle === 'nowrap'
                            ? `cmp-product-group-name-nowrap-${
                                  sanitizedGroupId ?? 'na'
                              }`
                            : 'cmp-product-group-name-wrap'
                    }
                    className={`embla__container w-full flex flex-row gap-6 items-center ${
                        groupNameDisplayStyle === 'wrap'
                            ? groupNamePosition === 'end'
                                ? 'lg:justify-end'
                                : groupNamePosition === 'center'
                                  ? 'lg:justify-center'
                                  : ''
                            : ''
                    }`}
                    role="tablist"
                    aria-orientation="horizontal"
                >
                    {productGroupItems.map((group, index: number) => {
                        const label = group?.attributes?.displayName; // React จะ escape ให้ จึงปลอดภัยต่อ XSS
                        if (!label) return null;
                        const active = tabId === index;
                        const safeBtnId = sanitizeDomId(group?.id);
                        return (
                            <button
                                type="button"
                                role="tab"
                                aria-label="Tab Button"
                                aria-selected={active}
                                aria-controls={`pg-tabpanel-${
                                    sanitizedGroupId ?? 'na'
                                }-${index}`}
                                id={`pg-tab-${
                                    sanitizedGroupId ?? 'na'
                                }-${index}`}
                                key={safeBtnId ?? index}
                                className={`embla__slide shrink-0 py-4 font-semibold text-lg leading-[100%] text-center inline-flex whitespace-nowrap px-6 rounded-full cursor-pointer dynamic-bg dynamic-color focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                                style={
                                    {
                                        '--dynamic-bg': active
                                            ? colorStyle?.buttonBgHover ||
                                              '#254b7c'
                                            : 'transparent',
                                        '--dynamic-hover-bg':
                                            colorStyle?.buttonBgHover ||
                                            '#254b7c',
                                        '--dynamic-color': active
                                            ? colorStyle?.buttonTextHover ||
                                              '#ffffff'
                                            : colorStyle?.link || '#009eca',
                                        '--dynamic-hover-color':
                                            colorStyle?.buttonTextHover ||
                                            '#ffffff',
                                        '--dynamic-focus-color':
                                            colorStyle?.buttonTextHover ||
                                            '#ffffff',
                                        borderTopLeftRadius:
                                            radiusStyle?.buttonRadiusTopLeft ??
                                            '6px',
                                        borderTopRightRadius:
                                            radiusStyle?.buttonRadiusTopRight ??
                                            '6px',
                                        borderBottomLeftRadius:
                                            radiusStyle?.buttonRadiusBottomLeft ??
                                            '6px',
                                        borderBottomRightRadius:
                                            radiusStyle?.buttonRadiusBottomRight ??
                                            '6px',
                                        minWidth: 'fit-content',
                                    } as React.CSSProperties
                                }
                                onClick={() =>
                                    handleClickTab(index, groupNameDisplayStyle)
                                }
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div
                id={`product-showboxes-component-tab-divider-${sanitizedGroupId}`}
                className="h-px w-full my-4"
                style={{
                    backgroundColor: colorStyle ? colorStyle.text : '#3d3d3d',
                    opacity: 0.2,
                }}
            />
        </div>
    );
};

export default ProductShowBoxesTab;
